import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import clsx from 'clsx';

// ---- Mock data ----
const buildMockBusiness = (id) => ({
  id: parseInt(id),
  name: 'Creative Arts Studio',
  type: 'business',
  description:
    'A premier destination for art workshops, creative events, and cultural experiences. We specialise in hands-on learning and community building through art.',
  email: 'hello@creativeartstudio.com',
  phone: '+1 (555) 123-4567',
  website: 'https://creativeartstudio.com',
  address: '123 Art Street, Creative District, CA 90210',
  profileImage: '/api/placeholder/150/150',
  coverImage: '/api/placeholder/800/300',
  rating: 4.7,
  reviewCount: 89,
  followerCount: 456,
  eventCount: 234,
  categories: ['Arts & Culture', 'Education', 'Workshops'],
  socialLinks: {
    instagram: 'https://instagram.com/creativeartstudio',
    facebook: 'https://facebook.com/creativeartstudio',
    twitter: 'https://twitter.com/creativeartstudio',
  },
  businessHours: {
    monday: '9:00 AM - 6:00 PM',
    tuesday: '9:00 AM - 6:00 PM',
    wednesday: '9:00 AM - 6:00 PM',
    thursday: '9:00 AM - 6:00 PM',
    friday: '9:00 AM - 8:00 PM',
    saturday: '10:00 AM - 8:00 PM',
    sunday: 'Closed',
  },
  amenities: ['Free Wi-Fi', 'Parking Available', 'Wheelchair Accessible', 'Art Supplies Provided'],
  since: '2018',
});

const mockEvents = [
  {
    id: 1,
    title: 'Pottery Making Workshop',
    description: 'Learn the basics of pottery in this hands-on workshop',
    datetime: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000),
    price: 75,
    attendees: 8,
    maxAttendees: 12,
    image: '/api/placeholder/300/200',
    category: 'Workshop',
  },
  {
    id: 2,
    title: 'Abstract Painting Class',
    description: 'Express yourself through abstract art techniques',
    datetime: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
    price: 60,
    attendees: 5,
    maxAttendees: 10,
    image: '/api/placeholder/300/200',
    category: 'Class',
  },
  {
    id: 3,
    title: 'Art Gallery Opening Night',
    description: 'Join us for the opening of our new exhibition',
    datetime: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000),
    price: 25,
    attendees: 45,
    maxAttendees: 60,
    image: '/api/placeholder/300/200',
    category: 'Event',
  },
];

const mockReviews = [
  {
    id: 1,
    userName: 'Sarah Johnson',
    userAvatar: '/api/placeholder/40/40',
    rating: 5,
    comment: 'Amazing pottery workshop! The instructor was knowledgeable and patient. Highly recommend!',
    eventTitle: 'Pottery Making Workshop',
    date: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000),
  },
  {
    id: 2,
    userName: 'Mike Chen',
    userAvatar: '/api/placeholder/40/40',
    rating: 4,
    comment: 'Great atmosphere and excellent facilities. The painting class was very inspiring.',
    eventTitle: 'Abstract Painting Class',
    date: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000),
  },
  {
    id: 3,
    userName: 'Emily Rodriguez',
    userAvatar: '/api/placeholder/40/40',
    rating: 5,
    comment: 'Beautiful gallery space and wonderful community of artists. Will definitely be back!',
    eventTitle: 'Art Gallery Opening Night',
    date: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000),
  },
];

// ---- Helpers ----
function formatDate(date) {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

function StarRating({ rating, reviewCount }) {
  return (
    <div className="flex items-center gap-1.5">
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <svg
            key={star}
            className={clsx('w-4 h-4', star <= rating ? 'text-warning-400' : 'text-surface-200')}
            fill="currentColor"
            viewBox="0 0 20 20"
            aria-hidden="true"
          >
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        ))}
      </div>
      <span className="text-body-sm font-semibold text-surface-700">{rating}</span>
      {reviewCount !== undefined && (
        <span className="text-body-sm text-surface-400">({reviewCount} reviews)</span>
      )}
    </div>
  );
}

// ---- Skeleton ----
function BusinessViewSkeleton() {
  return (
    <div className="max-w-6xl mx-auto animate-pulse">
      <div className="h-64 md:h-80 skeleton rounded-2xl mb-0" />
      <div className="bg-white rounded-2xl shadow-soft-md mx-4 -mt-16 relative z-10 p-6 mb-8">
        <div className="flex gap-5">
          <div className="w-24 h-24 rounded-2xl skeleton flex-shrink-0" />
          <div className="flex-1 space-y-3 pt-1">
            <div className="h-7 w-48 skeleton rounded-xl" />
            <div className="h-4 w-64 skeleton rounded-xl" />
            <div className="h-4 w-full skeleton rounded-xl" />
            <div className="h-4 w-3/4 skeleton rounded-xl" />
          </div>
        </div>
      </div>
    </div>
  );
}

// ---- Social icon map ----
const SocialIcon = ({ platform }) => {
  if (platform === 'instagram') {
    return (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
      </svg>
    );
  }
  if (platform === 'facebook') {
    return (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
      </svg>
    );
  }
  if (platform === 'twitter') {
    return (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z" />
      </svg>
    );
  }
  return <span className="capitalize text-body-sm">{platform}</span>;
};

// ---- Main component ----
const BusinessView = () => {
  const { id } = useParams();
  const [business, setBusiness] = useState(null);
  const [events, setEvents] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [isFollowing, setIsFollowing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('events');
  const [showContactModal, setShowContactModal] = useState(false);
  const [partnershipRequest, setPartnershipRequest] = useState('');
  const [sendingPartnership, setSendingPartnership] = useState(false);
  const [hoursExpanded, setHoursExpanded] = useState(false);

  useEffect(() => {
    fetchBusinessData();
    fetchEvents();
    fetchReviews();
    checkFollowStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchBusinessData = async () => {
    setLoading(true);
    try {
      setBusiness(buildMockBusiness(id));
    } catch (error) {
      toast.error('Failed to load business details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchEvents = async () => {
    try {
      setEvents(mockEvents);
    } catch (error) {
      toast.error('Failed to load events for this business.');
    }
  };

  const fetchReviews = async () => {
    try {
      setReviews(mockReviews);
    } catch (error) {
      toast.error('Failed to load reviews for this business.');
    }
  };

  const checkFollowStatus = async () => {
    try {
      setIsFollowing(false);
    } catch (error) {
      // Non-critical — silently ignore
    }
  };

  const handleFollow = async () => {
    const next = !isFollowing;
    setIsFollowing(next);
    try {
      // Replace with actual API call
      toast.success(next ? `You are now following ${business?.name}` : `Unfollowed ${business?.name}`);
    } catch (error) {
      setIsFollowing(!next);
      toast.error('Could not update follow status. Please try again.');
    }
  };

  const sendPartnershipRequest = async () => {
    if (!partnershipRequest.trim()) return;
    setSendingPartnership(true);
    try {
      // Replace with actual API call
      await new Promise((r) => setTimeout(r, 600)); // simulate network
      setPartnershipRequest('');
      setShowContactModal(false);
      toast.success('Partnership request sent successfully!');
    } catch (error) {
      toast.error('Failed to send partnership request. Please try again.');
    } finally {
      setSendingPartnership(false);
    }
  };

  const tabs = [
    { key: 'events', label: 'Events', count: events.length },
    { key: 'reviews', label: 'Reviews', count: reviews.length },
  ];

  if (loading) return <BusinessViewSkeleton />;

  if (!business) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center px-4">
        <div className="w-16 h-16 rounded-2xl bg-surface-100 text-surface-400 flex items-center justify-center mb-5">
          <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
        </div>
        <h1 className="text-h2 text-surface-900 mb-2">Business Not Found</h1>
        <p className="text-body text-surface-500 mb-6">The business you are looking for does not exist.</p>
        <Link
          to="/"
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-white text-body-sm font-semibold rounded-xl hover:bg-primary-600 transition-colors duration-200"
        >
          Go Home
        </Link>
      </div>
    );
  }

  const todayKey = new Date().toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
  const todayHours = business.businessHours[todayKey] || 'Closed';

  return (
    <div className="max-w-6xl mx-auto font-sans">
      {/* Cover banner */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="relative h-56 md:h-72 rounded-2xl overflow-hidden bg-gradient-to-br from-primary-600 to-primary-900"
      >
        <img
          src={business.coverImage}
          alt={`${business.name} cover`}
          className="w-full h-full object-cover mix-blend-overlay opacity-60"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
      </motion.div>

      {/* Profile card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, delay: 0.15 }}
        className="bg-white rounded-2xl shadow-soft-md mx-4 md:mx-8 -mt-16 relative z-10 p-6 mb-8"
      >
        <div className="flex flex-col md:flex-row gap-5">
          {/* Avatar */}
          <div className="flex-shrink-0">
            <img
              src={business.profileImage}
              alt={business.name}
              className="w-24 h-24 rounded-2xl border-4 border-white shadow-soft-md object-cover"
            />
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-4">
              <div>
                <h1 className="text-h1 text-surface-900 mb-2">{business.name}</h1>
                <div className="flex flex-wrap gap-2 mb-3">
                  {business.categories.map((cat, i) => (
                    <span key={i} className="bg-primary-50 text-primary-700 px-2.5 py-1 rounded-full text-caption font-semibold">
                      {cat}
                    </span>
                  ))}
                </div>
                <StarRating rating={business.rating} reviewCount={business.reviewCount} />
              </div>

              <div className="flex items-center gap-3 flex-shrink-0">
                <button
                  onClick={handleFollow}
                  className={clsx(
                    'px-5 py-2.5 rounded-xl text-body-sm font-semibold transition-all duration-200',
                    isFollowing
                      ? 'bg-surface-100 text-surface-700 hover:bg-surface-200'
                      : 'bg-primary-500 text-white hover:bg-primary-600'
                  )}
                >
                  {isFollowing ? 'Following' : 'Follow'}
                </button>
                <button
                  onClick={() => setShowContactModal(true)}
                  className="px-5 py-2.5 bg-accent-500 text-white rounded-xl text-body-sm font-semibold hover:bg-accent-600 transition-colors duration-200"
                >
                  Partnership
                </button>
              </div>
            </div>

            {/* Stats pills */}
            <div className="flex flex-wrap gap-3 mb-4">
              {[
                { icon: (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                ), label: `${business.followerCount} followers` },
                { icon: (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                ), label: `${business.eventCount} events` },
                { icon: (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                ), label: `Since ${business.since}` },
              ].map((stat, i) => (
                <span key={i} className="inline-flex items-center gap-1.5 bg-surface-50 text-surface-600 px-3 py-1.5 rounded-full text-body-sm font-medium border border-surface-100">
                  <span className="text-primary-500">{stat.icon}</span>
                  {stat.label}
                </span>
              ))}
            </div>

            <p className="text-body text-surface-600 leading-relaxed">{business.description}</p>
          </div>
        </div>
      </motion.div>

      {/* Main grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Tabbed content */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.25 }}
          className="lg:col-span-2"
        >
          {/* Tab bar */}
          <div className="relative flex border-b border-surface-200 mb-6 gap-1">
            {tabs.map(({ key, label, count }) => {
              const isActive = activeTab === key;
              return (
                <button
                  key={key}
                  onClick={() => setActiveTab(key)}
                  className={clsx(
                    'relative px-4 py-3 text-body-sm font-semibold transition-colors duration-200 outline-none',
                    'focus-visible:ring-2 focus-visible:ring-primary-500/40 rounded-t-lg',
                    isActive ? 'text-primary-600' : 'text-surface-500 hover:text-surface-800'
                  )}
                >
                  {label}
                  <span className={clsx(
                    'ml-2 px-1.5 py-0.5 text-[11px] font-bold rounded-full',
                    isActive ? 'bg-primary-100 text-primary-600' : 'bg-surface-100 text-surface-500'
                  )}>
                    {count}
                  </span>
                  {isActive && (
                    <motion.div
                      layoutId="bv-tab-indicator"
                      className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-500 rounded-full"
                      transition={{ type: 'spring', stiffness: 500, damping: 35 }}
                    />
                  )}
                </button>
              );
            })}
          </div>

          <AnimatePresence mode="wait">
            {activeTab === 'events' && (
              <motion.div
                key="events"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.2 }}
                className="space-y-4"
              >
                {events.length > 0 ? (
                  events.map((event, i) => (
                    <motion.div
                      key={event.id}
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: i * 0.07 }}
                      className="bg-white border border-surface-100 rounded-2xl p-5 hover:shadow-soft-md transition-all duration-300 group"
                    >
                      <div className="flex flex-col md:flex-row gap-4">
                        <div className="relative overflow-hidden rounded-xl flex-shrink-0">
                          <img
                            src={event.image}
                            alt={event.title}
                            className="w-full md:w-44 h-32 object-cover transition-transform duration-500 group-hover:scale-105"
                            loading="lazy"
                          />
                          <span className="absolute top-2 right-2 bg-white/90 backdrop-blur-sm text-surface-700 px-2 py-0.5 rounded-lg text-caption font-semibold">
                            {event.category}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <Link
                            to={`/events/${event.id}`}
                            className="text-h3 text-surface-900 hover:text-primary-600 transition-colors duration-200 line-clamp-1 block mb-1"
                          >
                            {event.title}
                          </Link>
                          <p className="text-body-sm text-surface-500 mb-3 line-clamp-2">{event.description}</p>
                          <div className="flex flex-wrap gap-3 mb-4">
                            {[
                              { icon: (
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                              ), text: formatDate(event.datetime) },
                              { icon: (
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                              ), text: `${event.attendees}/${event.maxAttendees}` },
                              { icon: (
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                                </svg>
                              ), text: `$${event.price}` },
                            ].map((item, idx) => (
                              <span key={idx} className="inline-flex items-center gap-1.5 text-body-sm text-surface-500">
                                <span className="text-surface-400">{item.icon}</span>
                                {item.text}
                              </span>
                            ))}
                          </div>
                          <div className="flex gap-3">
                            <Link
                              to={`/events/${event.id}`}
                              className="bg-primary-500 text-white px-4 py-2 rounded-xl text-body-sm font-semibold hover:bg-primary-600 transition-colors duration-200"
                            >
                              View Details
                            </Link>
                            <button className="bg-success-500 text-white px-4 py-2 rounded-xl text-body-sm font-semibold hover:bg-success-600 transition-colors duration-200">
                              Join Event
                            </button>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))
                ) : (
                  <div className="flex flex-col items-center justify-center py-16 text-center">
                    <div className="w-14 h-14 rounded-2xl bg-primary-50 text-primary-400 flex items-center justify-center mb-4">
                      <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <h3 className="text-h3 text-surface-900 mb-2">No upcoming events</h3>
                    <p className="text-body text-surface-500">This business has no upcoming events scheduled.</p>
                  </div>
                )}
              </motion.div>
            )}

            {activeTab === 'reviews' && (
              <motion.div
                key="reviews"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.2 }}
                className="space-y-4"
              >
                {reviews.length > 0 ? (
                  reviews.map((review, i) => (
                    <motion.div
                      key={review.id}
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: i * 0.07 }}
                      className="bg-white border border-surface-100 rounded-2xl p-5"
                    >
                      <div className="flex gap-4">
                        <img
                          src={review.userAvatar}
                          alt={review.userName}
                          className="w-11 h-11 rounded-full object-cover flex-shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
                            <div>
                              <h4 className="text-body font-semibold text-surface-900">{review.userName}</h4>
                              <p className="text-body-sm text-primary-600">{review.eventTitle}</p>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <StarRating rating={review.rating} />
                              <p className="text-caption text-surface-400 mt-1">
                                {new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(review.date)}
                              </p>
                            </div>
                          </div>
                          <p className="text-body text-surface-600 leading-relaxed">{review.comment}</p>
                        </div>
                      </div>
                    </motion.div>
                  ))
                ) : (
                  <div className="flex flex-col items-center justify-center py-16 text-center">
                    <div className="w-14 h-14 rounded-2xl bg-primary-50 text-primary-400 flex items-center justify-center mb-4">
                      <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                      </svg>
                    </div>
                    <h3 className="text-h3 text-surface-900 mb-2">No reviews yet</h3>
                    <p className="text-body text-surface-500">Be the first to leave a review for this business.</p>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {/* Sidebar */}
        <motion.div
          initial={{ opacity: 0, x: 16 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.4, delay: 0.3 }}
          className="space-y-5"
        >
          {/* Contact card */}
          <div className="bg-white rounded-2xl shadow-soft-sm border border-surface-100 p-5">
            <h3 className="text-body font-semibold text-surface-900 mb-4">Contact Information</h3>
            <div className="space-y-3">
              {[
                {
                  icon: (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  ),
                  content: (
                    <a href={`mailto:${business.email}`} className="text-body-sm text-primary-600 hover:text-primary-700 break-all">
                      {business.email}
                    </a>
                  ),
                },
                {
                  icon: (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                  ),
                  content: (
                    <a href={`tel:${business.phone}`} className="text-body-sm text-primary-600 hover:text-primary-700">
                      {business.phone}
                    </a>
                  ),
                },
                {
                  icon: (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9v-9m0-9v9" />
                    </svg>
                  ),
                  content: (
                    <a href={business.website} target="_blank" rel="noopener noreferrer" className="text-body-sm text-primary-600 hover:text-primary-700">
                      Visit Website
                    </a>
                  ),
                },
                {
                  icon: (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  ),
                  content: <span className="text-body-sm text-surface-600">{business.address}</span>,
                },
              ].map((row, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-primary-50 text-primary-500 flex items-center justify-center flex-shrink-0">
                    {row.icon}
                  </div>
                  <div className="pt-1.5 min-w-0">{row.content}</div>
                </div>
              ))}
            </div>

            {/* Social links */}
            <div className="mt-5 pt-4 border-t border-surface-100">
              <p className="text-caption text-surface-500 font-semibold uppercase tracking-wide mb-3">Social Media</p>
              <div className="flex gap-2">
                {Object.entries(business.socialLinks).map(([platform, url]) => (
                  <a
                    key={platform}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-9 h-9 rounded-xl bg-surface-100 text-surface-500 hover:bg-primary-50 hover:text-primary-600 flex items-center justify-center transition-colors duration-200"
                    aria-label={platform}
                  >
                    <SocialIcon platform={platform} />
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Business hours card */}
          <div className="bg-white rounded-2xl shadow-soft-sm border border-surface-100 p-5">
            <button
              onClick={() => setHoursExpanded((v) => !v)}
              className="flex items-center justify-between w-full text-left"
              aria-expanded={hoursExpanded}
            >
              <div>
                <h3 className="text-body font-semibold text-surface-900">Business Hours</h3>
                <p className="text-caption text-surface-500 mt-0.5">
                  Today: <span className={clsx('font-semibold', todayHours === 'Closed' ? 'text-accent-600' : 'text-success-600')}>{todayHours}</span>
                </p>
              </div>
              <svg
                className={clsx('w-5 h-5 text-surface-400 transition-transform duration-200', hoursExpanded && 'rotate-180')}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            <AnimatePresence>
              {hoursExpanded && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.25 }}
                  className="overflow-hidden"
                >
                  <div className="mt-4 space-y-2 border-t border-surface-100 pt-4">
                    {Object.entries(business.businessHours).map(([day, hours]) => {
                      const isToday = day === todayKey;
                      return (
                        <div key={day} className={clsx('flex justify-between items-center py-1 px-2 rounded-lg', isToday && 'bg-primary-50')}>
                          <span className={clsx('capitalize text-body-sm', isToday ? 'font-semibold text-primary-700' : 'text-surface-700')}>
                            {day}
                          </span>
                          <span className={clsx(
                            'text-body-sm',
                            hours === 'Closed' ? 'text-accent-600 font-medium' : isToday ? 'text-primary-600 font-medium' : 'text-surface-500'
                          )}>
                            {hours}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Amenities card */}
          <div className="bg-white rounded-2xl shadow-soft-sm border border-surface-100 p-5">
            <h3 className="text-body font-semibold text-surface-900 mb-4">Amenities</h3>
            <div className="grid grid-cols-1 gap-2">
              {business.amenities.map((amenity, i) => (
                <div key={i} className="flex items-center gap-2.5">
                  <div className="w-5 h-5 rounded-full bg-success-50 text-success-600 flex items-center justify-center flex-shrink-0">
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <span className="text-body-sm text-surface-700">{amenity}</span>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>

      {/* Partnership modal */}
      <AnimatePresence>
        {showContactModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-xs flex items-center justify-center z-50 p-4"
            onClick={(e) => e.target === e.currentTarget && setShowContactModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 10 }}
              transition={{ duration: 0.25 }}
              className="bg-white rounded-2xl shadow-soft-xl w-full max-w-md p-6"
            >
              <div className="flex items-center justify-between mb-5">
                <div>
                  <h2 className="text-h3 text-surface-900">Partnership Request</h2>
                  <p className="text-body-sm text-surface-500 mt-1">to {business.name}</p>
                </div>
                <button
                  onClick={() => setShowContactModal(false)}
                  className="w-8 h-8 rounded-xl bg-surface-100 text-surface-500 hover:bg-surface-200 flex items-center justify-center transition-colors duration-200"
                  aria-label="Close dialog"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <p className="text-body text-surface-600 mb-4">
                Describe your business and how you would like to collaborate.
              </p>

              <textarea
                value={partnershipRequest}
                onChange={(e) => setPartnershipRequest(e.target.value)}
                placeholder="Hi! I'm interested in partnering with your business for co-hosted events..."
                rows={5}
                className="w-full px-4 py-3 bg-surface-50 border border-surface-200 rounded-xl text-body text-surface-900 placeholder-surface-400 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all duration-200 resize-none mb-4"
              />

              <div className="flex gap-3">
                <button
                  onClick={sendPartnershipRequest}
                  disabled={!partnershipRequest.trim() || sendingPartnership}
                  className="flex-1 bg-primary-500 text-white py-2.5 px-4 rounded-xl text-body-sm font-semibold hover:bg-primary-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-200"
                >
                  {sendingPartnership ? 'Sending...' : 'Send Request'}
                </button>
                <button
                  onClick={() => setShowContactModal(false)}
                  className="px-5 py-2.5 bg-surface-100 text-surface-700 rounded-xl text-body-sm font-semibold hover:bg-surface-200 transition-colors duration-200"
                >
                  Cancel
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default BusinessView;
